import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../services/settings.services';
import { IUser, IMailboxSettings } from '../../models/settings.models';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { Subject, takeUntil } from 'rxjs';
import {
  InboxDisconnectDialogComponent,
  InboxDisconnectChoice,
} from 'src/app/features/smart-inbox/components/inbox-view/inbox-disconnect-dialog/inbox-disconnect-dialog.component';

export interface ConnectionDetailsData {
  mailbox: IMailboxSettings;
}

interface UserRow {
  user: IUser;
  canView: boolean;
  canSend: boolean;
  selected: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// ConnectionDetailsModalComponent
// ─────────────────────────────────────────────────────────────────────────────
// Modal to view/edit a single mailbox connection: display name, sharing, user
// permissions (can view / can send). Responsibilities: load mailbox + users,
// update, disconnect, all errors via i18n.
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-connection-details-modal',
  templateUrl: './connection-details-modal.component.html',
  styleUrls: ['./connection-details-modal.component.scss'],
})
export class ConnectionDetailsModalComponent implements OnInit {
  // ── Public state ─────────────────────────────────────────────────────────
  mailbox: IMailboxSettings | null = null;
  form: FormGroup;
  allUsers: IUser[] = [];
  users: UserRow[] = [];
  loading = true;
  saving = false;
  disconnecting = false;
  deleting = false;
  error: string | null = null;

  dialogOptions = {
    titleIcon: 'inbox' as const,
    titleIconColor: 'secondary' as const,
    cancelText: '',
    cancelButtonStyle: 'transparent' as const,
    hideConfirmButton: true,
    customConfirmButton: true,
    size: 'large' as const,
  };

  private destroy$ = new Subject<void>();
  private defaultAllCanView = false;
  private defaultAllCanSend = false;

  constructor(
    public dialogRef: MatDialogRef<ConnectionDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConnectionDetailsData,
    private settingsService: SettingsService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private matDialog: MatDialog
  ) {
    this.form = new FormGroup({
      displayName: new FormControl(''),
      sharingType: new FormControl({ value: '', disabled: true }),
    });
  }

  get isTeam(): boolean {
    return this.mailbox?.mailbox_type === 'team';
  }

  get canViewCount(): number {
    return this.users.filter((user) => user.canView).length;
  }

  get canSendCount(): number {
    return this.users.filter((user) => user.canSend).length;
  }

  get totalUsers(): number {
    return this.users.length;
  }

  get canViewLabel(): string {
    const count = this.canViewCount;
    const total = this.totalUsers;
    if (total === 0) return '0';
    if (count === total) return `${this.translate.instant('settings.smart_inbox_can_view_all')} (${total})`;
    return `${count}/${total}`;
  }

  get canSendLabel(): string {
    const count = this.canSendCount;
    const total = this.totalUsers;
    if (total === 0) return '0';
    if (count === total) return `${this.translate.instant('settings.smart_inbox_can_view_all')} (${total})`;
    return `${count}/${total}`;
  }

  ngOnInit(): void {
    this.dialogOptions = { ...this.dialogOptions, cancelText: this.translate.instant('common.close') };
    this.mailbox = this.data?.mailbox ?? null;
    if (!this.mailbox) {
      this.loading = false;
      return;
    }
    this.form.patchValue({
      displayName: this.mailbox.name ?? '',
      sharingType: this.mailbox.mailbox_type ?? 'personal',
    });

    this.store.select((state) => state.settings.allUsers).pipe(takeUntil(this.destroy$)).subscribe((users) => {
      this.allUsers = users;
    });

    this.settingsService.getInboxMailbox(this.mailbox.id).subscribe({
      next: (res) => {
        const detail = (res as { object?: IMailboxSettings }).object ?? res.data?.[0] ?? this.mailbox!;
        this.mailbox = detail;
        this.form.patchValue({
          displayName: detail.name ?? '',
          sharingType: detail.mailbox_type ?? 'personal',
        });

        if (this.isTeam) {
          this.defaultAllCanView = (detail.can_view_ids?.length ?? 0) === 0;
          this.defaultAllCanSend = (detail.can_send_ids?.length ?? 0) === 0;
          this.users = this.allUsers.map((user) => ({
            user,
            canView: this.defaultAllCanView ? true : (detail.can_view_ids?.includes(user.id) ?? false),
            canSend: this.defaultAllCanSend ? true : (detail.can_send_ids?.includes(user.id) ?? false),
            selected: false,
          }));
          this.users.forEach((r) => {
            if (r.canSend && !r.canView) r.canSend = false;
          });

          // Sort with mailbox owner first
          this.users.sort((a, b) => {
            if (a.user.id === detail.mailbox_owner_id) return -1;
            if (b.user.id === detail.mailbox_owner_id) return 1;
            return 0;
          });
        }

        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.error ?? this.translate.instant('settings.smart_inbox_error_load_mailbox');
        this.loading = false;
      },
    });
  }

  // ── Actions ──────────────────────────────────────────────────────────────
  close(): void {
    this.dialogRef.close(false);
  }

  toggleCanView(row: UserRow): void {
    const selectedRows = this.users.filter((r) => r.selected);
    if (selectedRows.length > 0) {
      const next = !row.canView;
      selectedRows.forEach((r) => {
        r.canView = next;
        if (!next) r.canSend = false;
      });
    } else {
      row.canView = !row.canView;
      if (!row.canView) row.canSend = false;
    }
  }

  toggleCanSend(row: UserRow): void {
    const selectedRows = this.users.filter((r) => r.selected);
    if (selectedRows.length > 0) {
      const next = !row.canSend;
      selectedRows.forEach((r) => {
        if (r.canView) r.canSend = next;
      });
    } else {
      if (row.canView) row.canSend = !row.canSend;
    }
  }

  toggleUserSelection(row: UserRow): void {
    row.selected = !row.selected;
  }

  update(): void {
    if (!this.mailbox) return;
    this.error = null;
    this.saving = true;
    const payload: { name?: string; can_view?: number[]; can_send?: number[] } = {};
    payload.name = this.form.get('displayName')?.value?.trim() || undefined;
    if (this.isTeam && this.users.length > 0) {
      const allCanView = this.users.every((r) => r.canView);
      const allCanSend = this.users.every((r) => r.canSend);
      payload.can_view = allCanView ? [] : this.users.filter((r) => r.canView).map((r) => r.user.id);
      payload.can_send = allCanSend ? [] : this.users.filter((r) => r.canSend).map((r) => r.user.id);
    }
    this.settingsService.updateInboxMailbox(this.mailbox.id, payload).subscribe({
      next: (res) => {
        const updated = (res as { object?: IMailboxSettings }).object ?? (res as { data?: IMailboxSettings[] }).data?.[0];
        if (updated) this.mailbox = updated;
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.error = err?.error?.error ?? err?.message ?? this.translate.instant('settings.smart_inbox_error_update_failed');
        this.saving = false;
      },
    });
  }

  /**
   * Asks whether to disconnect (reversible) or delete (permanent); this used to disconnect
   * immediately with no confirmation. Switch on the exact literal — both are truthy.
   */
  disconnect(): void {
    if (!this.mailbox) return;
    const dialogRef = this.matDialog.open(InboxDisconnectDialogComponent, {
      panelClass: 'inbox-disconnect-dialog-panel',
      data: {
        email: this.mailbox.email,
        canDisconnect: this.mailbox.connection_status !== 'disconnected',
        canDelete: true,
      },
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((choice: InboxDisconnectChoice | undefined) => {
      switch (choice) {
        case 'disconnect_only':
          this.runDisconnect();
          break;
        case 'delete_connection':
          this.runDelete();
          break;
        default:
          break;
      }
    });
  }

  private runDisconnect(): void {
    if (!this.mailbox) return;
    this.error = null;
    this.disconnecting = true;
    this.settingsService.disconnectInboxMailbox(this.mailbox.id).subscribe({
      next: () => {
        this.disconnecting = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.error = err?.error?.error ?? err?.message ?? this.translate.instant('settings.smart_inbox_error_disconnect_failed');
        this.disconnecting = false;
      },
    });
  }

  private runDelete(): void {
    if (!this.mailbox) return;
    this.error = null;
    this.deleting = true;
    this.settingsService.deleteInboxMailbox(this.mailbox.id).subscribe({
      next: () => {
        this.deleting = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.error = err?.error?.error ?? err?.message ?? this.translate.instant('settings.smart_inbox_error_delete_failed');
        this.deleting = false;
      },
    });
  }

  // ── Labels ───────────────────────────────────────────────────────────────
  providerLabel(provider: string): string {
    const map: Record<string, string> = { gmail: 'Gmail', o365: 'Outlook', exchange: 'Exchange' };
    return map[provider] ?? provider;
  }
}
